import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { submitContactForm } from "../api/contactApi"; // <-- Import the API function

// Define the validation schema with Yup
const schema = yup
  .object({
    fullName: yup.string().required("Full Name is required"),
    email: yup
      .string()
      .email("Invalid email format")
      .required("Email is required"),
    // Relaxed phone validation slightly - max length check might be too strict depending on format
    phone: yup.string().required("Phone number is required"), //.max(10),
    subject: yup.string().required("Subject is required"),
    message: yup.string().required("Message is required"),
  })
  .required();

// Add additionalData prop
function ContactForm({ contactFormData, additionalData = null }) {
  const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  // Handle form submission
  const onSubmit = async (formData) => {
    setIsSubmitting(true); // Set loading true
    // Combine standard form data with additional data if provided
    const submissionData = additionalData
      ? { ...formData, ...additionalData }
      : formData;

    console.log("Form Submitted Data:", submissionData); // Log the final data

    try {
      // --- Call the backend API ---
      const response = await submitContactForm(submissionData);
      console.log("Backend Response:", response.data);

      // Show success toast message
      toast.success(response.data.message || "Message Sent Successfully!", {
        // Use message from backend if available
        position: "top-center",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      // Reset form fields
      reset();
    } catch (error) {
      console.error("Contact form submission error:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to send message. Please try again.";
      toast.error(errorMessage, {
        position: "top-center",
      });
    } finally {
      setIsSubmitting(false); // Set loading false
    }
  };

  if (!contactFormData) {
    return <div>Loading form...</div>; // Or some placeholder
  }

  return (
    <div
      style={{
        backgroundImage: `url(${contactFormData.bgImage})`,
      }}
      className="bg-cover bg-center background-no-repeat background-blend-multiply"
    >
      <div className="max-w-[1400px] mx-auto h-full flex flex-col md:flex-row justify-between pt-24 md:pt-48 pb-12 md:pb-24 px-4 md:px-0 text-left">
        <div className="space-y-8 md:space-y-16 w-full md:mr-16">
          <h1 className="text-5xl md:text-8xl font-medium">
            {contactFormData.title}
          </h1>
          <p className="text-xl md:text-2xl">{contactFormData.subtitle}</p>
        </div>
        <div className="w-full pt-6 md:pt-3">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col w-full space-y-5 items-start"
          >
            {/* --- Input fields remain the same --- */}
            <input
              type="text"
              className="w-full p-5 text-base md:text-2xl focus:outline-none"
              placeholder="Full Name"
              {...register("fullName")}
            />
            {errors.fullName && (
              <p className="text-red-500 text-sm md:text-base">
                {errors.fullName.message}
              </p>
            )}
            <input
              type="email"
              className="w-full p-5 text-base md:text-2xl focus:outline-none"
              placeholder="Email"
              {...register("email")}
            />
            {errors.email && (
              <p className="text-red-500 text-sm md:text-base">
                {errors.email.message}
              </p>
            )}
            <input
              // Changed type to tel for better mobile UX
              type="tel"
              className="w-full p-5 text-base md:text-2xl focus:outline-none"
              placeholder="Phone No."
              {...register("phone")}
            />
            {errors.phone && (
              <p className="text-red-500 text-sm md:text-base">
                {errors.phone.message}
              </p>
            )}
            <input
              type="text"
              className="w-full p-5 text-base md:text-2xl focus:outline-none"
              placeholder="Subject"
              {...register("subject")}
            />
            {errors.subject && (
              <p className="text-red-500 text-sm md:text-base">
                {errors.subject.message}
              </p>
            )}
            <textarea
              className="w-full p-5 text-base md:text-2xl focus:outline-none"
              placeholder="Message..."
              rows="5"
              {...register("message")}
            ></textarea>
            {errors.message && (
              <p className="text-red-500 text-sm md:text-base">
                {errors.message.message}
              </p>
            )}
            <button
              type="submit"
              disabled={isSubmitting} // Disable button while submitting
              className={`px-5 py-3 text-base md:text-2xl bg-[#cc673d] border-[#cc673d] text-white md:w-auto w-full cursor-pointer hover:bg-[#b85a36] transition-colors duration-200 ${
                isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
            >
              {isSubmitting ? "Sending..." : "Submit"}{" "}
              {/* Change button text */}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default ContactForm;